import GameConnectionsN from "@/Poker/io/GameConnectionsN";
import usePlayerDetails from "../data/usePlayerDetails";
import { 
  useGameId,
  useTableId,
  useSetBuyInValueForPlayerPosition,
  useGameDefinition,
  useIsOptForAutoReBuy,
  usePlayerTurn,
  useCallAmount,
  usePlayerSeat as useGamePlayerSeat
} from "../data/useGameTable";
import TableUtils from "@/Poker/utils/TableUtils";
import {
  AUTO_CALL_ACTION,
  AUTO_CALL_ANY_ACTION,
  AUTO_CHECK,
  AUTO_CHECK_ACTION,
  AUTO_CHECK_OR_FOLD_ACTION,
  AUTO_FOLD_ACTION,
  GAME_SOCKET_CONNECTED,
} from "@/Poker/data/Constants";
import { setRITRequest } from "@/Poker/redux/slices/gameTableSlice";
import { useDispatch } from "react-redux";
import useTableSettings from "../data/useTableSettings";

export default function useGameSocket(tempTableId) {
  const dispatch = useDispatch();
  const { userName, _id } = usePlayerDetails();
  const gameId = useGameId(tempTableId);
  const tableId = useTableId(tempTableId);
  const setBuyInValueForPlayerPosition = useSetBuyInValueForPlayerPosition(tempTableId);
  const gameDefinition = useGameDefinition(tempTableId);
  const isOptForAutoReBuy = useIsOptForAutoReBuy(tempTableId);
  const playerTurn = usePlayerTurn(tempTableId);
  const callAmount = useCallAmount(tempTableId);
  const playerSeat = useGamePlayerSeat(tempTableId);
  const { tableSettings } = useTableSettings();

  if (!tempTableId) {
    console.error("TEMP TABLE ID MUST BE PASSED TO useGameSocket HOOK.");
    return {};
  }

  const sendGameSocketMessage = (message, payload) => {
    let socketPayload = {
      playerName: userName,
      userId: _id,
      gameId,
      tableId,
      tempTableId,
      ...payload,
    };
    let protocol = `${message}#` + JSON.stringify(socketPayload);
    let gameConnection =
      GameConnectionsN.getInstance().getGameConnection(tempTableId);
    gameConnection.listener.getCommunicationChannel().sendMessage(protocol);
  };

  const sendReserveProtocol = (seat) => {
    let data = {
      position: seat,
    };
    sendGameSocketMessage("reserve", data);
  };

  const sendDReserveProtocol = () => {
    let data = {
      position: setBuyInValueForPlayerPosition,
    };
    sendGameSocketMessage("dreserve", data);
  };

  const sendDRebuyInProtocol = (autoRebuyValue) => {
    const data = {
      position: playerSeat,
      gameDefId: gameDefinition?.gameDefId,
      isUserOptForAutoReBuy: autoRebuyValue,
    };
    console.log("DRebuy Payload is ", data);
    sendGameSocketMessage("DRebuy", data);
  };

  const sendJoinProtocol = (amount, autoRebuyValue) => {
    const data = {
      gameDefId: gameDefinition?.gameDefId,
      position: setBuyInValueForPlayerPosition,
      buyInAmount: Number(amount),
      isUserOptForAutoReBuy: autoRebuyValue,
    };
    sendGameSocketMessage("join", data);
  };

  const sendTopUpProtocol = (amount) => {
    let payload = {
      topUpAmount: amount,
    };
    sendGameSocketMessage('topup', payload);
  }

  const sendRebuyInProtocol = (amount, autoRebuyValue) => {
    let data = {
      gameDefId: gameDefinition?.gameDefId,
      position: playerSeat,
      gameDefId: gameDefinition?.gameDefId,
      buyInAmount: Number(amount),
      isUserOptForAutoReBuy: autoRebuyValue,
    };
    sendGameSocketMessage("ReBuy", data);
  };

  const sendLeaveSeatProtocol = () => {
    sendGameSocketMessage("leave");
  };

  const sendExitGameProtocol = () => {
    sendGameSocketMessage("close");
  };

  const sendFoldProtocol = () => {
    sendGameSocketMessage("fold");
  };

  const sendCallProtocol = () => {
    const data = {
      betAmount: callAmount,
    };
    sendGameSocketMessage("call", data);
  };

  const sendRaiseProtocol = (raiseAmount) => {
    const data = {
      betAmount: raiseAmount,
    };
    sendGameSocketMessage("raise", data);
  };

  const sendCheckProtocol = () => {
    sendGameSocketMessage("check");
  };

  const sendAllInProtocol = () => { };

  const sendShowCardsProtocol = (playerCards) => {
    const data = {
      cards: playerCards,
    };
    sendGameSocketMessage("showcards", data);
  };

  const triggerExitTableFlow = () => {
    console.log(
      "🔍 [triggerExitTableFlow] Starting exit table flow for tableId:",
      tempTableId
    );

    let tableObserver = TableUtils.getInstance().getTableObserver(tempTableId);
    console.log("🔍 [triggerExitTableFlow] Got tableObserver:", tableObserver);

    if (
      tableObserver &&
      tableObserver?.getGameConnectionStatus() !== GAME_SOCKET_CONNECTED
    ) {
      console.log(
        "🔍 [triggerExitTableFlow] TableObserver exists but game connection status is not CONNECTED. Status:",
        tableObserver?.getGameConnectionStatus()
      );
      console.log(
        "🔍 [triggerExitTableFlow] Removing table observer for tableId:",
        tempTableId
      );
      TableUtils.getInstance().removeTableObserver(tempTableId); //deleteGameTable reducer method will be invoked from table observer 'cleanUp' method
      console.log(
        "🔍 [triggerExitTableFlow] Table observer removed successfully"
      );
    } else {
      console.log(
        "🔍 [triggerExitTableFlow] Either no tableObserver or game connection is CONNECTED. Getting game connection..."
      );
      let gameConnection =
        GameConnectionsN.getInstance().getGameConnection(tempTableId);
      console.log(
        "🔍 [triggerExitTableFlow] Got gameConnection:",
        gameConnection
      );

      if (gameConnection) {
        console.log(
          "🔍 [triggerExitTableFlow] Game connection exists, closing listener..."
        );
        gameConnection.listener.close();
        console.log(
          "🔍 [triggerExitTableFlow] Game connection listener closed successfully"
        );
      } else {
        console.log(
          "🔍 [triggerExitTableFlow] No game connection available, manually removing table observer..."
        );
        //Note: As game connection is not available we are not restricting user from eixt table, we manually deleting the table and making him exit.
        TableUtils.getInstance()?.removeTableObserver(tempTableId);
        console.log(
          "🔍 [triggerExitTableFlow] Table observer manually removed"
        );
      }
    }
    console.log("🔍 [triggerExitTableFlow] Exit table flow completed");
  };

  const sendRITAck = (ritOpt) => {
    const data = {
      optedInToRIT: ritOpt,
    };
    dispatch(
      setRITRequest({
        tempTableId: tempTableId,
        value: false,
      })
    );
    sendGameSocketMessage("RITAck", data);
  };

  const sendAutoActionProtocol = (action, value) => {
    const returnProtocolName = (action) => {
      switch (action) {
        case AUTO_CALL_ACTION:
          return "AutoCall";
        case AUTO_FOLD_ACTION:
          return "AutoFold";
        case AUTO_CALL_ANY_ACTION:
          return "AutoCallAny";
        case AUTO_CHECK_ACTION:
          return "AutoCheck";
        case AUTO_CHECK_OR_FOLD_ACTION:
          return "AutoCheckFold";
      }
    };
    let data = {
      autoActionEnable: value,
    };
    sendGameSocketMessage(returnProtocolName(action), data);
  };

  const sendSitInProtocol = () => {
    sendGameSocketMessage("SitIn");
  };

  const sendSitOutProtocol = () => {
    sendGameSocketMessage("SitOut");
  };

  const sendCancelSitOutProtocol = () => {
    sendGameSocketMessage("SitOutCancel");
  };

  const sendPlayNextRequest = (isWaitingForBigBlind) => {
    const data = {
        isWaitForBB: isWaitingForBigBlind,
    }
    sendGameSocketMessage("wfbb", data);
  }

  const sendPlayerConfigProtocol = () => {
    const {
      showHandStrength,
      autoTableSwitch,
      autoMuck,
      showEmojis,
      showThrowables,
      smartGestures,
      smartGestureOptions,
      BBDisplay,
      sound,
      haptics,
      autoRebuy,
      autoTopupConfig,
      bettingOptions,
      autoBuyIn,
    } = tableSettings;
    const {
      enabled,
      fallsBelowAmount,
      topUpAmount,
      fallBelowMinAmount,
      fallsBelowMaxAmount,
      topUpMinAmount,
      topUpMaxAmount,
    } = autoTopupConfig;

    const remapPresets = (presetValues) => {
      return Object.keys(presetValues).reduce((acc, key) => {
        acc[`preset${parseInt(key) + 1}`] = presetValues[key];
        return acc;
      }, {});
    };

    const updatedBettingOptions = {
      ...bettingOptions,
      preflopPresetValues: remapPresets(bettingOptions.preflopPresetValues),
      postflopPresetValues: remapPresets(bettingOptions.postflopPresetValues),
    };

    const requestPayload = {
      playerName: userName,
      playerBasicSettings: {
        handStrength: showHandStrength,
        autoTableSwitching: autoTableSwitch,
        displayChipsInBB: BBDisplay,
        autoMuck: autoMuck,
        emojiEnabled: showEmojis,
        throwableEnabled: showThrowables,
      },
      playerHaptics: {
        vibrations: haptics,
        sound: sound,
        smartGestures: smartGestures,
        doubleTapOnCheck: smartGestureOptions.smartCheck,
        dragToFold: smartGestureOptions.smartFold,
      },
      playerAutoRebuyConfig: {
        autoRebuy: autoRebuy,
        autoBuyIn: autoBuyIn.enabled,
        autoBuyInAmount: autoBuyIn.percentage,
        playerAutoTopUpConfig: {
          autoTopUpenabled: enabled,
          fallsBelowPercentage: fallsBelowAmount,
          topUpPercentage: topUpAmount,
          fallBelowMinAmount: fallBelowMinAmount,
          fallsBelowMaxAmount: fallsBelowMaxAmount,
          topUpMinAmount: topUpMinAmount,
          topUpMaxAmount: topUpMaxAmount,
        },
      },
      bettingOptions: updatedBettingOptions,
    };

    sendGameSocketMessage("PlayerConfig", requestPayload);
  };

  const sendTourneyRankingLeaderboardRequest = (tourneyId) => {
    const data = {
      tourneyId: tourneyId
    }
    sendGameSocketMessage("TourneyRankLeaderboard", data);
  }

  const sendDisableAutoPlay = (tourneyId) => {
    const data = {
      tourneyId: tourneyId
    }
    sendGameSocketMessage('tourneyautoplaysitin', data);
  }
  const sendDealVoteRequest = (payload) => {
    console.log("Sending Deal Vote Request with payload:", payload);
    sendGameSocketMessage("DealVote", payload);
  };

  const sendDealICMVoteRequest = (payload) => {
    sendGameSocketMessage("DealICMVote", payload);
  };

  return {
    sendGameSocketMessage,
    sendReserveProtocol,
    sendDReserveProtocol,
    sendDRebuyInProtocol,
    sendJoinProtocol,
    sendRebuyInProtocol,
    sendLeaveSeatProtocol,
    sendExitGameProtocol,
    triggerExitTableFlow,
    sendCallProtocol,
    sendCheckProtocol,
    sendRaiseProtocol,
    sendFoldProtocol,
    sendAllInProtocol,
    sendShowCardsProtocol,
    sendRITAck,
    sendAutoActionProtocol,
    sendTopUpProtocol,
    sendSitInProtocol,
    sendSitOutProtocol,
    sendCancelSitOutProtocol,
    sendPlayNextRequest,
    sendPlayerConfigProtocol,
    sendTourneyRankingLeaderboardRequest,
    sendDisableAutoPlay,
    sendDealVoteRequest,
    sendDealICMVoteRequest,
  };
}
